// @flow
import {ofType} from 'redux-observable';
import {of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {SKIP_INITIAL_CONFIG_STORAGE_KEY} from '../../router/PrivateRoute';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {SaveAccountSettingsSuccess} from '../profile/duck';
import {
  getSessionApi,
  refreshSessionApi,
  loginApi,
  logoutApi,
  sendEmailForgotPasswordApi,
  resetPasswordApi,
  getDomainChangeTokenApi,
  // registerApi,
} from './api';

import {
  FreeTrialSubscribeSuccess,
  PerformSubscribeSuccess,
} from '../checkout/duck';
import * as Sentry from '@sentry/react';
import {getBasicDomain} from '../../utils/component';

import type {User} from '../../entities';
import type {Action} from '../../store/utils';
import type {ActionsObservable, StateObservable} from 'redux-observable';
import type {RootState} from '../index';
import type {ProfileAction} from '../profile/duck';
import type {ApiError} from '../../http/error';

export type AuthAction = Action & {
  email?: string,
  error?: string,
  password?: string,
  user?: User,
  token?: string,
};

export type AuthState = {
  user?: User,
  isAuthenticating?: boolean,
  loginError?: string,
  registerError?: string,
  initialAuthDone?: boolean,
  resetPasswordError?: string,
  isResettingPassword?: boolean,
  resetPasswordSuccess?: boolean,
  domainChangeToken?: string,
  isFetchingDomainChangeToken?: boolean,
};

export class GetSession extends ActionCreator<AuthAction> {}
export class GetSessionSuccess extends ActionCreator<AuthAction> {}
export class GetSessionErrorFn extends ActionCreator<AuthAction> {}
// export class GetSession extends ActionCreator<AuthAction> {}
export class RefreshSessionSuccess extends ActionCreator<AuthAction> {}
export class RefreshSessionErrorFn extends ActionCreator<AuthAction> {}
export class Login extends ActionCreator<AuthAction> {}
export class LoginSuccess extends ActionCreator<AuthAction> {}
export class LoginErrorFn extends ActionCreator<AuthAction> {}
export class Register extends ActionCreator<AuthAction> {}
export class RegisterSuccess extends ActionCreator<AuthAction> {}
export class RegisterErrorFn extends ActionCreator<AuthAction> {}
export class Logout extends ActionCreator<AuthAction> {}
export class AuthResetState extends ActionCreator<AuthAction> {}
export class AuthNoop extends ActionCreator<AuthAction> {}
export class SendEmailRecovery extends ActionCreator<AuthAction> {}
export class ResetPassword extends ActionCreator<AuthAction> {}
export class ResetPasswordSuccess extends ActionCreator<AuthAction> {}
export class ResetPasswordErrorFn extends ActionCreator<AuthAction> {}
export class GetDomainChangeToken extends ActionCreator<AuthAction> {}
export class GetDomainChangeTokenSuccess extends ActionCreator<AuthAction> {}
export class GetDomainChangeTokenErrorFn extends ActionCreator<AuthAction> {}
export class RetryLogin extends ActionCreator<AuthAction> {}
export class RefreshSession extends ActionCreator<AuthAction> {}

export const actions = {
  getSession: (payload?: AuthAction) => new GetSession(payload),
  login: (payload: AuthAction) => new Login(payload),
  logout: (payload: AuthAction) => new Logout(payload),
  authResetState: (payload: AuthAction) => new AuthResetState(payload),
  sendEmailRecovery: (payload: AuthAction) => new SendEmailRecovery(payload),
  resetPassword: (payload: AuthAction) => new ResetPassword(payload),
  getDomainChangeToken: () => new GetDomainChangeToken(),
  refreshSession: () => new RefreshSession(),
};

export const initialState = {};

export default createReducer(initialState, {
  [SaveAccountSettingsSuccess.type]: (
    state: AuthState,
    action: ProfileAction
  ) => ({
    ...state,
    user: action.user,
  }),
  [GetSession.type]: () => ({isAuthenticating: true}),
  [GetSessionSuccess.type]: (_, action: AuthAction) => ({
    user: action.user,
    initialAuthDone: true,
  }),
  [GetSessionErrorFn.type]: () => ({initialAuthDone: true}),
  // [RefreshSession.type]: () => ({isAuthenticating: true}),
  [RefreshSessionSuccess.type]: (state: AuthState) => ({
    ...state,
  }),
  [RefreshSessionErrorFn.type]: (state: AuthState) => ({...state}),
  [Login.type]: (state: AuthState) => ({
    ...state,
    isAuthenticating: true,
    loginError: undefined,
  }),
  [LoginSuccess.type]: (state: AuthState, action: AuthAction) => ({
    ...state,
    user: action.user,
    isAuthenticating: false,
    loginError: undefined,
  }),
  [LoginErrorFn.type]: (state: AuthState, action: AuthAction) => ({
    ...state,
    loginError: action.error,
    isAuthenticating: false,
    registerError: undefined,
  }),
  [Register.type]: (state: AuthState) => ({...state, isAuthenticating: true}),
  [RegisterSuccess.type]: (state: AuthState, action: AuthAction) => ({
    ...state,
    user: action.user,
    isAuthenticating: false,
    registerError: undefined,
  }),
  [RegisterErrorFn.type]: (state: AuthState, action: AuthAction) => ({
    ...state,
    registerError: action.error,
    isAuthenticating: false,
  }),
  [Logout.type]: () => ({...initialState, initialAuthDone: true}),
  [AuthResetState.type]: () => initialState,
  [AuthNoop.type]: (state: AuthState) => state,
  [ResetPassword.type]: (state: AuthState) => ({
    ...state,
    isResettingPassword: true,
  }),
  [ResetPasswordSuccess.type]: (state: AuthState) => ({
    ...state,
    isResettingPassword: false,
    resetPasswordSuccess: true,
  }),
  [ResetPasswordErrorFn.type]: (state: AuthState, action: AuthAction) => ({
    ...state,
    resetPasswordError: action.error,
    isResettingPassword: false,
  }),
  [GetDomainChangeToken.type]: (state: AuthState) => ({
    ...state,
    isFetchingDomainChangeToken: true,
  }),
  [GetDomainChangeTokenSuccess.type]: (
    state: AuthState,
    action: AuthAction
  ) => ({
    ...state,
    domainChangeToken: action.token,
    isFetchingDomainChangeToken: false,
  }),
  [GetDomainChangeTokenErrorFn.type]: (
    state: AuthState,
    action: AuthAction
  ) => ({
    ...state,
    isFetchingDomainChangeToken: false,
    fetchDomainChangeTokenError: action.error,
  }),
});

export const getSessionEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(GetSession.type),
    switchMap(({token}) =>
      getSessionApi(token ? {token: token} : undefined).pipe(
        map((user: User) => new GetSessionSuccess({user})),
        buildCatchError(GetSessionErrorFn)
      )
    )
  );

export const refreshSessionEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(
      PerformSubscribeSuccess.type,
      FreeTrialSubscribeSuccess.type,
      RefreshSession.type
    ),
    switchMap(() =>
      refreshSessionApi().pipe(
        map(
          () =>
            new RefreshSessionSuccess({
              domain:
                state$.value &&
                state$.value.store &&
                state$.value.store.store &&
                state$.value.store.store.domains &&
                getBasicDomain(state$.value.store.store.domains),
            })
        ),
        buildCatchError(RefreshSessionErrorFn)
      )
    )
  );

export const sendEmailRecoveryEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(SendEmailRecovery.type),
    switchMap((action: AuthAction) =>
      sendEmailForgotPasswordApi({email: action.emailRecovery}).pipe(
        map(() => new AuthNoop()),
        buildCatchError(AuthNoop)
      )
    )
  );

export const resetPasswordEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(ResetPassword.type),
    switchMap((action: AuthAction) =>
      resetPasswordApi({token: action.token, password: action.password}).pipe(
        map(() => new ResetPasswordSuccess()),
        buildCatchError(ResetPasswordErrorFn)
      )
    )
  );

export const getDomainChangeTokenEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(GetDomainChangeToken.type),
    switchMap(() =>
      getDomainChangeTokenApi().pipe(
        map(({token}) => new GetDomainChangeTokenSuccess({token})),
        buildCatchError(GetDomainChangeTokenErrorFn)
      )
    )
  );

export const retryLoginEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(RetryLogin.type),
    switchMap((action: AuthAction) =>
      loginApi({
        email: action.email,
        password: action.password,
      }).pipe(
        map((user: User) => new LoginSuccess({user})),
        buildCatchError(LoginErrorFn)
      )
    )
  );

export const loginEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(Login.type),
    switchMap((action: AuthAction) =>
      loginApi({
        email: action.email,
        password: action.password,
      }).pipe(
        map((user: User) => {
          localStorage.removeItem(SKIP_INITIAL_CONFIG_STORAGE_KEY);
          return new GetSession();
        }),
        catchError((apiError: ApiError) => {
          if (apiError && apiError.error && apiError.error.error) {
            let {error, message} = apiError.error;
            if (error === 'SessionExpiredException') {
              return of(
                new RetryLogin({
                  email: action.email,
                  password: action.password,
                })
              );
            }
            return of(
              new LoginErrorFn({
                error: message,
              })
            );
          }
          return of(
            new LoginErrorFn({
              error: 'Unexpected server error',
            })
          );
        })
      )
    )
  );

/*export const registerEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(Register.type),
    switchMap((action: AuthAction) =>
      registerApi({
        firstName: action.firstName,
        lastName: action.lastName,
        email: action.email,
        password: action.password,
      }).pipe(
        map((user: User) => new RegisterSuccess({user})),
        buildCatchError(RegisterErrorFn, error.response.message)
      )
    )
  );*/

export const logoutEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(Logout.type),
    switchMap(() =>
      logoutApi().pipe(
        map(() => {
          Sentry.configureScope(scope => scope.setUser(null));
          return new AuthNoop();
        }),
        buildCatchError(AuthNoop)
      )
    )
  );
